import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  ImportStep, 
  ImportSession, 
  ImportProgress, 
  ImportError,
  ImportFormData,
  ParsedChannelData,
  StepNavigationState
} from '@/features/channels/types/import';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger, LOG_CONTEXTS } from '@/utils/logger';
import { importService } from '@/services/import/importService';

interface ImportState {
  // Step Management
  currentStep: ImportStep;
  canNavigateBack: boolean;
  
  // Form Data
  formData: Partial<ImportFormData>;
  previewData: ParsedChannelData[] | null;
  
  // Session & Progress
  currentSession: ImportSession | null;
  progress: ImportProgress | null;
  errors: ImportError[];
  isImporting: boolean;
  
  // Modal State
  isModalOpen: boolean;

  // Navigation Actions
  setStep: (step: ImportStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  
  // Form Actions
  updateFormData: (data: Partial<ImportFormData>) => void;
  setPreviewData: (data: ParsedChannelData[] | null) => void;
  
  // Session Actions
  setSession: (session: ImportSession | null) => void;
  updateProgress: (progress: ImportProgress) => void;
  addErrors: (errors: ImportError[]) => void;
  clearErrors: () => void;
  
  // Modal Actions
  openModal: () => void;
  closeModal: () => void;
  
  // Import Actions
  setIsImporting: (isImporting: boolean) => void;
  reset: () => void;
  completeReset: () => void;

  // Selectors
  getNavigationState: () => StepNavigationState;
  getStepData: () => any;
  getValidationState: () => { isValid: boolean; errors: ImportError[] };

  // Add startImport to ImportState
  startImport: () => Promise<void>;

  resetImportState: () => void;

  // Add a new function to handle successful import completion
  handleImportSuccess: () => void;
}

const INITIAL_STATE = {
  currentStep: 'form' as ImportStep,
  canNavigateBack: false,
  formData: {},
  previewData: null,
  currentSession: null,
  progress: null,
  errors: [],
  isImporting: false,
  isModalOpen: false
};

// Create a unique store ID for logging
const STORE_ID = `ImportStore_${Date.now()}`;

export const useImportStore = create<ImportState>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      // Navigation Actions
      setStep: (step) => {
        logger.debug(LOG_CONTEXTS.IMPORT, `Setting step in ${STORE_ID}`, { 
          from: get().currentStep, 
          to: step 
        });
        set({ currentStep: step });
      },
      
      goToNextStep: () => {
        const { currentStep } = get();
        const steps: ImportStep[] = ['form', 'preview', 'progress'];
        const currentIndex = steps.indexOf(currentStep);
        
        if (currentIndex < steps.length - 1) {
          const nextStep = steps[currentIndex + 1];
          
          // Add a safety check to prevent rapid multiple transitions
          // Only transition if we're not already at the target step
          if (currentStep !== nextStep) {
            logger.info(LOG_CONTEXTS.IMPORT, `Moving to next step in ${STORE_ID}`, { 
              from: currentStep, 
              to: nextStep 
            });
            
            set({ 
              currentStep: nextStep,
              canNavigateBack: true
            });
          } else {
            logger.warn(LOG_CONTEXTS.IMPORT, `Attempted duplicate transition to ${nextStep} in ${STORE_ID}`);
          }
        } else {
          logger.warn(LOG_CONTEXTS.IMPORT, `Cannot go to next step in ${STORE_ID} (already at last step)`, {
            currentStep
          });
        }
      },
      
      goToPreviousStep: () => {
        const { currentStep } = get();
        const steps: ImportStep[] = ['form', 'preview', 'progress'];
        const currentIndex = steps.indexOf(currentStep);
        
        if (currentIndex > 0) {
          const prevStep = steps[currentIndex - 1];
          logger.info(LOG_CONTEXTS.IMPORT, `Moving to previous step in ${STORE_ID}`, { 
            from: currentStep, 
            to: prevStep 
          });
          
          set({ 
            currentStep: prevStep,
            canNavigateBack: currentIndex - 1 > 0
          });
        } else {
          logger.warn(LOG_CONTEXTS.IMPORT, `Cannot go to previous step in ${STORE_ID} (already at first step)`, {
            currentStep
          });
        }
      },

      // Form Actions
      updateFormData: (data) => {
        logger.debug(LOG_CONTEXTS.IMPORT, `Updating form data in ${STORE_ID}`, {
          currentData: get().formData,
          newData: data
        });
        
        set((state) => ({
          formData: { ...state.formData, ...data }
        }));
      },
      
      setPreviewData: (data) => {
        logger.info(LOG_CONTEXTS.IMPORT, `Setting preview data in ${STORE_ID}`, { 
          dataCount: data?.length || 0,
          hasData: !!data
        });
        
        set({ previewData: data });
      },

      // Session Actions
      setSession: (session) => {
        logger.info(LOG_CONTEXTS.IMPORT, `Setting import session in ${STORE_ID}`, { 
          sessionId: session?.id,
          hasSession: !!session
        });
        
        set({ currentSession: session });
      },
      
      updateProgress: (progress) => {
        logger.debug(LOG_CONTEXTS.IMPORT, `Updating progress in ${STORE_ID}`, { 
          progress,
          sessionId: get().currentSession?.id
        });
        
        set({ progress });
      },
      
      addErrors: (errors) => {
        logger.warn(LOG_CONTEXTS.IMPORT, `Adding errors in ${STORE_ID}`, { 
          newErrorCount: errors.length,
          currentErrorCount: get().errors.length,
          errors
        });
        
        set((state) => ({
          errors: [...state.errors, ...errors]
        }));
      },
      
      clearErrors: () => {
        logger.info(LOG_CONTEXTS.IMPORT, `Clearing errors in ${STORE_ID}`, { 
          errorCount: get().errors.length 
        });
        
        set({ errors: [] });
      },

      // Modal Actions
      openModal: () => {
        logger.debug(LOG_CONTEXTS.IMPORT, `Opening modal in ${STORE_ID}`);
        set({ isModalOpen: true });
      },
      
      closeModal: () => {
        logger.debug(LOG_CONTEXTS.IMPORT, `Closing modal in ${STORE_ID}`);
        set({ isModalOpen: false });
      },

      // Import Actions
      setIsImporting: (isImporting) => {
        logger.debug(LOG_CONTEXTS.IMPORT, `Setting isImporting in ${STORE_ID}`, { isImporting });
        set({ isImporting });
      },
      
      reset: () => {
        logger.info(LOG_CONTEXTS.IMPORT, `Resetting store (keeping form data) in ${STORE_ID}`);
        const currentState = get();
        set({
          ...INITIAL_STATE,
          formData: currentState.formData,
          previewData: currentState.previewData
        });
      },

      completeReset: () => {
        logger.info(LOG_CONTEXTS.IMPORT, `Complete reset of store in ${STORE_ID}`);
        set(INITIAL_STATE);
        window.localStorage.removeItem('import-store');
      },

      // Selectors
      getNavigationState: () => {
        const { currentStep, isImporting } = get();
        const steps: ImportStep[] = ['form', 'preview', 'progress'];
        const currentIndex = steps.indexOf(currentStep);

        const state = {
          currentStep,
          canGoNext: currentIndex < steps.length - 1 && !isImporting,
          canGoPrevious: currentIndex > 0 && !isImporting
        };
        
        logger.debug(LOG_CONTEXTS.IMPORT, `Navigation state calculated in ${STORE_ID}`, { state });
        
        return state;
      },

      getStepData: () => {
        const { currentStep, formData, previewData, progress } = get();
        let data;
        
        switch (currentStep) {
          case 'form':
            data = formData;
            break;
          case 'preview':
            data = previewData;
            break;
          case 'progress':
            data = progress;
            break;
          default:
            data = null;
        }
        
        logger.debug(LOG_CONTEXTS.IMPORT, `Step data retrieved in ${STORE_ID}`, { 
          step: currentStep,
          hasData: !!data
        });
        
        return data;
      },

      getValidationState: () => {
        const { errors } = get();
        const state = {
          isValid: errors.length === 0,
          errors
        };
        
        logger.debug(LOG_CONTEXTS.IMPORT, `Validation state calculated in ${STORE_ID}`, { 
          isValid: state.isValid,
          errorCount: errors.length
        });
        
        return state;
      },

      // Add startImport to ImportState
      startImport: async () => {
        // Create a request ID for tracking this import operation
        const requestId = logger.createRequestId();
        
        try {
          const { formData, previewData } = get();
          set({ isImporting: true });
          
          logger.info(LOG_CONTEXTS.IMPORT, `Starting import in ${STORE_ID}`, {
            providerId: formData.providerId,
            versionId: formData.versionId,
            totalChannels: previewData?.length || 0
          }, requestId);

          // Safety checks
          if (!formData.providerId || !formData.versionId || !previewData || previewData.length === 0) {
            logger.error(LOG_CONTEXTS.IMPORT, `Cannot start import with invalid data in ${STORE_ID}`, { 
              hasProviderId: !!formData.providerId,
              hasVersionId: !!formData.versionId,
              hasPreviewData: !!previewData,
              dataLength: previewData?.length
            });

            toast.error("Invalid import data. Please try again.");
            return;
          }

          // Check if we're already on the progress step with an active session
          if (get().currentStep === 'progress' && get().currentSession) {
            logger.info(LOG_CONTEXTS.IMPORT, `Import already in progress in ${STORE_ID}`, {
              sessionId: get().currentSession?.id
            }, requestId);
            return;
          }

          logger.info(LOG_CONTEXTS.IMPORT, `Creating import session in ${STORE_ID}`, { 
            providerId: formData.providerId,
            versionId: formData.versionId,
            itemCount: previewData.length
          }, requestId);
          
          const { data: session, error: sessionError } = await supabase
            .from('import_sessions')
            .insert({
              provider_id: formData.providerId,
              version_id: formData.versionId,
              format_type: formData.format || 'name_first',
              category_id: formData.categoryId,
              plan_ids: formData.planIds || [],
              status: 'pending',
              total_items: previewData.length,
              processed_items: 0,
              error_log: [],
              metadata: {
                is_local: formData.is_local || false,
                is_duplicate: formData.is_duplicate || false
              }
            })
            .select()
            .single();

          if (sessionError) {
            logger.error(LOG_CONTEXTS.IMPORT, `Session creation failed in ${STORE_ID}`, { 
              error: sessionError 
            }, requestId);
            
            throw sessionError;
          }

          // Initialize progress tracking
          const initialProgress = {
            total: previewData.length,
            processed: 0,
            success: 0, 
            failed: 0,
            errors: []
          };
          
          // Set the initial progress and current session
          set({ 
            progress: initialProgress,
            currentSession: {
              ...session,
              status: (session.status as any) || 'pending',
              total_items: session.total_items || 0,
              processed_items: session.processed_items || 0,
              error_log: (session.error_log as any) || []
            } 
          });
          
          logger.info(LOG_CONTEXTS.IMPORT, `Initialized progress tracking in ${STORE_ID}`, initialProgress, requestId);

          // Move to progress step first
          set({ currentStep: 'progress' });
          
          // Allow a small delay for the UI to update before starting the import process
          setTimeout(async () => {
            logger.info(LOG_CONTEXTS.IMPORT, `Starting import process in ${STORE_ID}`, { 
              sessionId: session.id,
              dataCount: previewData.length
            }, requestId);
            
            try {
              // Use the import service instead of direct RPC call to ensure flags are passed
              const importOptions = {
                providerId: formData.providerId!,
                versionId: formData.versionId!,
                categoryId: formData.categoryId!,
                planIds: formData.planIds || [],
                is_local: formData.is_local || false,
                is_duplicate: formData.is_duplicate || false
              };
              
              const result = await importService.startImport(session.id, previewData, importOptions);
    
              logger.info(LOG_CONTEXTS.IMPORT, `Import completed successfully in ${STORE_ID}`, { 
                sessionId: session.id,
                result
              }, requestId);
    
              toast.success(`${previewData.length} channels have been successfully imported`);
            } catch (error) {
              // Handle errors but keep session active for progress monitoring
              logger.error(LOG_CONTEXTS.IMPORT, `Import RPC call failed in ${STORE_ID}`, { 
                error 
              }, requestId);
              
              toast.error(error instanceof Error ? error.message : "Failed to import channels");
            }
          }, 500);
        } catch (error) {
          logger.error(LOG_CONTEXTS.IMPORT, `Import failed in ${STORE_ID}`, { error }, requestId);
          
          toast.error(error instanceof Error ? error.message : "Failed to start import");
        } finally {
          set({ isImporting: false });
        }
      },

      // Add a new function to handle successful import completion
      handleImportSuccess: () => {
        const currentSession = get().currentSession;
        const currentProgress = get().progress;
        
        if (currentSession && currentProgress) {
          // Calculate final progress state
          const finalProgress = {
            ...currentProgress,
            processed: currentSession.total_items,
            success: currentSession.total_items - (currentSession.error_log?.length || 0),
            failed: currentSession.error_log?.length || 0
          };
          
          set({ progress: finalProgress });
          logger.info(LOG_CONTEXTS.IMPORT, `Updated final progress in ${STORE_ID}`, finalProgress);
        }
        
        // Use window.localStorage directly with the same key as in persist config
        window.localStorage.removeItem('import-store');
        get().completeReset();
        
        toast.success("All channels have been successfully imported");
      },

      resetImportState: () => {
        // Get current state to preserve specific parts we want to keep
        const currentState = get();
        
        set({
          currentStep: 'form' as ImportStep,
          canNavigateBack: false,
          formData: {
            format: 'name_first',
            categoryId: undefined,
            planIds: [],
            lastNumber: '0'
          },
          // Preserve the preview data to prevent it from disappearing during step transitions
          previewData: currentState.previewData, 
          currentSession: null,
          progress: null,
          errors: [],
          isImporting: false,
          isModalOpen: false
        });
      },
    }),
    {
      name: 'import-store',
      partialize: (state) => {
        logger.debug(LOG_CONTEXTS.IMPORT, `Persisting state in ${STORE_ID}`, {
          step: state.currentStep,
          hasFormData: !!state.formData,
          hasPreviewData: !!state.previewData,
          hasSession: !!state.currentSession
        });
        
        return {
          formData: state.formData,
          previewData: state.previewData,
          currentStep: state.currentStep,
          currentSession: state.currentSession,
          progress: state.progress,
          isModalOpen: state.isModalOpen
        };
      },
      storage: {
        getItem: (name) => {
          const str = window.localStorage.getItem(name);
          if (!str) return null;
          try {
            return JSON.parse(str);
          } catch (e) {
            window.localStorage.removeItem(name);
            return null;
          }
        },
        setItem: (name, value) => {
          window.localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          window.localStorage.removeItem(name);
        }
      }
    }
  )
);

// Add hooks for common operations
export const useImportProgress = () => {
  const store = useImportStore();
  
  const startImport = (session: ImportSession) => {
    store.reset();
    store.setSession(session);
    store.setIsImporting(true);
  };

  const completeImport = () => {
    store.handleImportSuccess();
  };

  const handleError = (error: ImportError) => {
    store.addErrors([error]);
    store.setIsImporting(false);
  };

  return {
    ...store,
    startImport,
    completeImport,
    handleError
  };
};
